import { zodResolver } from "@hookform/resolvers/zod";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { useAppDispatch } from "../app/hooks";
import { loginSuccess } from "../features/login/login-slice";
import {
	auth,
	registerWithEmailAndPassword,
	signInWithGoogle,
} from "../firebase";

const registerSchema = z.object({
	email: z.string().email("Enter a valid email address"),
	password: z
		.string()
		.min(8, "Password must be at least 8 characters")
		.regex(/[A-Z]/, "Password must contain at least one uppercase letter")
		.regex(/[a-z]/, "Password must contain at least one lowercase letter")
		.regex(/\d/, "Password must contain at least one digit"),
});

type RegisterFormValues = z.infer<typeof registerSchema>;

function Register() {
	const [user, loading, authError] = useAuthState(auth);
	const [formError, setFormError] = useState<string | null>(null);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
		watch,
	} = useForm<RegisterFormValues>({
		resolver: zodResolver(registerSchema),
		mode: "onChange",
	});
	const emailValue = watch("email", "");
	const passwordValue = watch("password", "");

	useEffect(() => {
		if (user) {
			dispatch(
				loginSuccess({
					username: user.displayName || user.email || "",
					email: user.email || "",
					uid: user.uid,
				})
			);
			navigate("/dashboard");
		} else if (authError) {
			setFormError(authError.message);
		}
	}, [authError, dispatch, navigate, user]);

	const onSubmit = async (values: RegisterFormValues) => {
		setFormError(null);
		try {
			await registerWithEmailAndPassword(values.email, values.password);
		} catch (error) {
			if (error instanceof Error) {
				setFormError(error.message);
			} else {
				setFormError("Unable to register right now. Please try later.");
			}
		}
	};

	const handleGoogleRegister = async () => {
		setFormError(null);
		try {
			await signInWithGoogle();
		} catch (error) {
			if (error instanceof Error) {
				setFormError(error.message);
			} else {
				setFormError("Unable to register with Google.");
			}
		}
	};

	return (
		<section className="page page--auth">
			<div className="auth-card">
				<header>
					<p className="eyebrow">Crea tu espacio</p>
					<h1>Registra tu cuenta</h1>
					<p>Sin tarjetas de crédito y con auditorías ilimitadas.</p>
				</header>
				<form className="auth-form" onSubmit={handleSubmit(onSubmit)} noValidate>
					<label htmlFor="register-email">Correo electrónico</label>
					<input
						id="register-email"
						type="email"
						className={clsx("auth-input", {
							"has-value": Boolean(emailValue),
							"input-error": Boolean(errors.email),
						})}
						placeholder="correo@empresa.com"
						autoComplete="email"
						{...register("email")}
					/>
					{errors.email && (
						<span className="form-error" role="alert">
							{errors.email.message}
						</span>
					)}

					<label htmlFor="register-password">Contraseña</label>
					<input
						id="register-password"
						type="password"
						className={clsx("auth-input", {
							"has-value": Boolean(passwordValue),
							"input-error": Boolean(errors.password),
						})}
						placeholder="********"
						autoComplete="new-password"
						{...register("password")}
					/>
					{errors.password && (
						<span className="form-error" role="alert">
							{errors.password.message}
						</span>
					)}

					<button
						className="button button--primary button--full"
						type="submit"
						disabled={isSubmitting || loading}
					>
						Crear cuenta
					</button>
					<button
						className="button button--ghost button--full"
						type="button"
						onClick={handleGoogleRegister}
						disabled={isSubmitting || loading}
					>
						Registrarme con Google
					</button>

					{formError && (
						<p className="form-error" role="alert">
							{formError}
						</p>
					)}

					<div className="auth-form__links">
						<Link to="/login">Ya tengo cuenta</Link>
					</div>
				</form>
			</div>
		</section>
	);
}
export default Register;
